/// <reference path="module.ts" />

/**
 * Appointment Book — Web Component Bootstrap
 *
 * Single-call registration with the generic web component framework.
 * The framework handles: loader creation, gate evaluation, bundle loading,
 * element mounting, bridge injection, and legacy fallback — all automatically.
 */
angular.module('ApptBookApp').run([
    '$injector',
    '$rootScope',
    'meevo.AppSettings',
    'meevo.components.common.BusinessInfoDataService',
    'meevo.plugins.interop.InteropService',
    'meevo.components.common.IdentityService',
    'webComponentSetup',
    '$mdSidenav',
    '$state',
    'appointmentBookNewExperienceBannerService',
    function (
        $injector: angular.auto.IInjectorService,
        $rootScope: angular.IRootScopeService,
        appSettings: Meevo.Components.Common.IAppSettings,
        businessInfoDataService: Meevo.Components.Common.IBusinessInfoDataService,
        interopService: Meevo.Plugins.Interop.InteropService,
        identityService: Meevo.Components.Common.IIdentityService,
        webComponentSetup: any,
        $mdSidenav: ng.material.ISidenavService,
        $state: angular.ui.IStateService,
        bannerService: any,
    ) {
        // Holds a reference to the mounted custom element so that the
        // APPLY_PARAMS_TO_CONTEXT listener below can dispatch DOM events to it.
        var mountedWcElement: HTMLElement | null = null;

        // Buffer for convo params that arrive before the web component mounts.
        // APPLY_PARAMS_TO_CONTEXT fires during the route transition, before the bundle
        // finishes loading and onMount is called. We store the normalised detail here
        // and replay it in onMount so no params are silently dropped.
        var pendingConvoDetail: any = null;

        // Buffer for an EditAppointment open request that arrives before the WC mounts.
        // Date navigation is buffered separately in pendingConvoDetail / preMountProps.
        var pendingOpenDetail: { appointmentServiceId: string; appointmentId: string; autoEdit: boolean } | null = null;
        var pendingManualRebookServices: any[] | null = null;

        // returnStateInfo from the most recent EditAppointment payload. The Shell
        // stores this (not the WC) so it can broadcast GOTO_STATE when the WC
        // fires meevo:edit-complete after the user saves or cancels the edit.
        var pendingReturnStateInfo: { returnStateSection: string; id?: any; initialEditSection?: any; actionState?: any; targetState?: any } | null = null;

        // Live experience-swap (no reload) state:
        //  - lastWcExperienceState caches the web component's current date/view and
        //    employee/resource view, pushed via its experienceStateChange output, so a
        //    new→legacy swap can seed the legacy book with the same context.
        //  - pendingLegacySeedParams holds the applyParams payload (plus the
        //    employee/resource view) to hand the legacy controller once it re-mounts
        //    (WEB_COMPONENT_MOUNTED, mode 'legacy').
        type WcBookView = 'employee' | 'resource';
        var lastWcExperienceState: { date: string; viewMode: 'day' | 'week'; bookView: WcBookView } | null = null;
        var pendingLegacySeedParams: { dates: Date[]; scrollToCurrentDate: boolean; isEmployeeView: boolean } | null = null;

        // One-shot "sanity-check the gate against the live API" flag. Set when the
        // user requests a switch to the new experience and consumed by the gate
        // resolver on the next evaluation. The gating business settings are a
        // per-location toggle another user can flip at any time, but both the
        // gate's preferences cache and the banner's APP_BUSINFOSETTINGS snapshot
        // are loaded once at app startup and never refreshed. Without a forced
        // re-fetch the switch would mount the enhanced book against stale settings.
        var revalidateOnNextGate = false;

        // Experience switching (US-1..US-4)
        type ExperienceState = {
            eligible: boolean;
            centralOfficeEnabled: boolean;
            locationEnabled: boolean;
            offered: boolean;
            isNewExperienceEnabled: boolean;
            shouldRenderEnhanced: boolean;
        };

        function getIdentitySafe(): Meevo.Components.Common.IIdentity | null {
            try {
                return identityService.current ? identityService.current() : null;
            } catch (error) {
                return null;
            }
        }

        function isCentralOfficeBusiness(identity: Meevo.Components.Common.IIdentity | null): boolean {
            if (!identity) {
                return false;
            }
            // A central-office *login* has centralOfficeId === locationId. Locations
            // *under* a central office also carry centralOfficeId (the CO's id) but with
            // a different locationId — those MUST stay gated by the central-office toggle,
            // so they are deliberately NOT treated as central office here. Testing only
            // centralOfficeId > 0 (the previous behaviour) let under-CO locations bypass
            // the toggle, so disabling it at the central office never forced them back to
            // legacy. Mirrors IdentityService.inCentralOfficeMode().
            return typeof identity.centralOfficeId === 'number'
                && identity.centralOfficeId > 0
                && identity.centralOfficeId === identity.locationId;
        }

        var APPOINTMENT_BOOK_AREA_KEY = 'HEADER_APPOINTMENT_BOOK';

        function getStoredExperienceChoice(): boolean {
            return !!bannerService.getStoredChoice(APPOINTMENT_BOOK_AREA_KEY);
        }

        function setStoredExperienceChoice(isNewExperienceEnabled: boolean): void {
            bannerService.setStoredChoice(isNewExperienceEnabled, APPOINTMENT_BOOK_AREA_KEY);
        }

        // Shown when the just-in-time gate re-check finds the enhanced book is no
        // longer offered (a gating setting was changed before the click landed).
        // OK-only dialog — mirrors the discard-confirm modal the banner already uses.
        function notifyEnhancedNoLongerAvailable(): void {
            try {
                var modal: any = $injector.get('appointmentBookModalService');
                var $translate: any = $injector.get('$translate');
                modal.showMessage($translate.instant('MESSAGE_SWITCH_EXPERIENCE_UNAVAILABLE'), {
                    buttonsType: Meevo.Model.ButtonTypeEnum.Ok,
                });
            } catch (error) {
                console.warn('[AppointmentBook] unable to show enhanced-unavailable message', error);
            }
        }

        function resolveExperienceState(preferences: any): ExperienceState {
            var identity = getIdentitySafe();
            var settings = preferences.appointmentBookSettings || {};
            // enhancedBookingMode is the database-only master eligibility flag (no UI).
            var eligible = settings.enhancedBookingMode === true;
            var locationEnabled = settings.enableEnhancedBookingModeAtLocation === true;
            var centralOfficeEnabled = settings.enableEnhancedBookingModeAtCentralOffice === true;
            var isCentralOffice = isCentralOfficeBusiness(identity);
            var isSingleLocation = !!identity && identity.isSingleLocation === true;
            var centralOfficeCheck = isCentralOffice || isSingleLocation ? true : centralOfficeEnabled;
            var offered = eligible && locationEnabled && centralOfficeCheck;
            var isNewExperienceEnabled = getStoredExperienceChoice();
            var shouldRenderEnhanced = offered && isNewExperienceEnabled;

            return {
                eligible: eligible,
                centralOfficeEnabled: centralOfficeEnabled,
                locationEnabled: locationEnabled,
                offered: offered,
                isNewExperienceEnabled: isNewExperienceEnabled,
                shouldRenderEnhanced: shouldRenderEnhanced,
            };
        }

        // Listen for convobar / context navigation params broadcast by main.controller.
        // When the appointment book is the target state, normalise all resolved params
        // (date, view mode, employee/gender/price-level filters, time scroll) and forward
        // them as a single meevo:apply-convo-params CustomEvent to the mounted web component.
        function pad2(n: number): string {
            return n < 10 ? '0' + n : '' + n;
        }

        function toLocalIsoDate(d: Date): string {
            return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
        }

        function dispatchConvoDetail(wcElement: HTMLElement, detail: any): void {
            wcElement.dispatchEvent(new CustomEvent('meevo:apply-convo-params', {
                detail: detail,
                bubbles: false,
            }));
        }

        function dispatchManualRebook(wcElement: HTMLElement, appointmentServices: any[]): void {
            wcElement.dispatchEvent(new CustomEvent('meevo:manual-rebook', {
                detail: { appointmentServices: appointmentServices },
                bubbles: false,
            }));
        }

        $rootScope.$on(Meevo.Components.Common.Messages.APPLY_PARAMS_TO_CONTEXT, function (event, state, params) {
            if (!state || state.name !== appSettings.ROUTE_NAMES.appointmentBook) {
                return;
            }
            if (!params) {
                return;
            }

            if (params.actionState === Meevo.Model.AppointmentBookState.RebookAppointment
                && params.actionStatePayload
                && params.actionStatePayload.appointmentServices) {
                var manualRebookServices = params.actionStatePayload.appointmentServices;
                var rebookRegistry = $injector.get<any>('webComponentRegistry');
                rebookRegistry.isEnabled('appointment-book').then(function (useEnhanced: boolean) {
                    if (!useEnhanced || !Array.isArray(manualRebookServices) || manualRebookServices.length === 0) {
                        return;
                    }

                    if (mountedWcElement && (mountedWcElement as any).isConnected) {
                        var wcForRebook = mountedWcElement;
                        setTimeout(function () {
                            dispatchManualRebook(wcForRebook, manualRebookServices);
                        }, 0);
                    } else {
                        pendingManualRebookServices = manualRebookServices;
                    }
                });
            }

            // #130068: Confirmation Manager / Client Find send actionState = DeleteAppointment.
            // Launch the legacy cancellation reason modal directly — the enhanced AB
            // webcomponent does not handle external cancel requests yet.
            // Mirrors legacy AppointmentBookActionsService.deleteAppointment (lines 4802-4834):
            // fetch appointment header → set appointHeaderDelete → show modal.
            if (params.actionState === Meevo.Model.AppointmentBookState.DeleteAppointment
                && params.actionStatePayload) {
                // #130620: This host listener is registered unconditionally by the
                // .run() block, but in LEGACY mode the AppointmentBookController is
                // mounted and ALSO handles APPLY_PARAMS_TO_CONTEXT → DeleteAppointment
                // (processAction → deleteAppointment → showDeleteDialog). Firing here
                // in LEGACY mode produces TWO cancellation dialogs. Only own the cancel
                // modal when the enhanced web component is the active renderer
                var registry = $injector.get<any>('webComponentRegistry');
                registry.isEnabled('appointment-book').then(function (useEnhanced: boolean) {
                    if (!useEnhanced) {
                        // Legacy AppointmentBookController owns the cancellation modal.
                        return;
                    }

                    var actionsService = $injector.get<any>('appointmentBookActionsService');
                    var modalService = $injector.get<any>('appointmentBookModalService');
                    var globals = $injector.get<any>('appointmentBookGlobalsService');
                    var $http = $injector.get<angular.IHttpService>('$http');
                    var apiBase = (sessionStorage.getItem('BASE_URL') || '') + '/api/';

                    var payload = params.actionStatePayload;
                    var idsToDelete = payload.appointmentServiceIdsToDelete || [payload.id];
                    actionsService.appointmentServiceIdsToDelete = idsToDelete;
                    actionsService.deleteAppointReason = null;
                    // #130068: Propagate returnStateInfo so the legacy
                    // checkReturnToState navigates back to the caller
                    // (e.g. Confirmation Manager) after cancel completes.
                    actionsService.returnStateInfo = payload.returnStateInfo || undefined;
                    globals.deletedIds = [];
                    globals.apptServicesBeingCancelledWithFee = [];

                    // Fetch appointment data + ensure businessInfo is populated
                    // (legacy builder sets globals.businessInfo on init, but the
                    // enhanced AB skips that path).
                    var apptPromise = $http.get(apiBase + 'Appointment/' + payload.appointmentId);
                    var settingsPromise = globals.businessInfo
                        ? Promise.resolve(null)
                        : businessInfoDataService.getBusinessInfoPreferences();

                    Promise.all([apptPromise, settingsPromise]).then(function (results: any[]) {
                        var apptData = results[0].data;
                        apptData.services.forEach(function (svc: any) {
                            svc.client = { id: svc.clientId, firstName: svc.clientFirstName, lastName: svc.clientLastName };
                            svc.startTime = svc.startDateTime;
                            svc.startDate = new Date(new Date(svc.startDateTime).setHours(0, 0, 0, 0));
                        });
                        actionsService.appointHeaderDelete = apptData;

                        if (results[1]) {
                            globals.businessInfo = globals.businessInfo || {};
                            var abSettings = results[1].appointmentBookSettings || {};
                            var regSettings = results[1].registerSettings || {};
                            globals.businessInfo.requireCancellationReasonWhenDeletingAnAppointment =
                                abSettings.requireCancellationReasonWhenDeletingAnAppointment || false;
                            globals.businessInfo.enableIntegratedRegister =
                                regSettings.enableIntegratedRegister || false;
                        }

                        modalService.showDeleteDialog(idsToDelete);
                    });
                });
                return;
            }

            // #130189: Confirmation Manager / Client Find send actionState = EditAppointment.
            // The legacy sidenav editor (md-component-id="right") is not compiled in
            // enhanced-AB mode, so appointmentBookActionsService.openAppointment() never
            // resolves. Instead:
            //   1. Dispatch meevo:apply-convo-params so the existing convoParamsHandler
            //      navigates to the appointment's date (same path as any date-nav).
            //   2. Dispatch meevo:open-appointment so the WC synchronously opens the
            //      view-appointment panel (no loadGrid wait — panel fetches its own data).
            if (params.actionState === Meevo.Model.AppointmentBookState.EditAppointment
                && params.actionStatePayload) {
                var editPayload = params.actionStatePayload;
                var editDateRaw = editPayload.appointmentDate;
                var editDateStr: string | null = null;
                if (editDateRaw) {
                    if (typeof editDateRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(editDateRaw)) {
                        editDateStr = editDateRaw;
                    } else {
                        var editDateObj = editDateRaw instanceof Date ? editDateRaw : new Date(editDateRaw);
                        if (!isNaN(editDateObj.getTime())) {
                            editDateStr = toLocalIsoDate(editDateObj);
                        }
                    }
                }

                if (editDateStr) {
                    pendingReturnStateInfo = editPayload.returnStateInfo || null;
                    var openDetail = {
                        appointmentServiceId: String(editPayload.id),
                        appointmentId: String(editPayload.appointmentId),
                        autoEdit: true,
                    };
                    var navDetail = {
                        date: editDateStr,
                        viewMode: 'day',
                        employeeIds: [],
                        clearFilters: false,
                        gender: null,
                        priceLevels: [],
                        time: null,
                        scrollToCurrentTime: false,
                        columnHeaderOrder: null,
                    };

                    if (mountedWcElement && (mountedWcElement as any).isConnected) {
                        var wcElForOpen = mountedWcElement;
                        var openDetailForDispatch = openDetail;
                        var navDetailForDispatch = navDetail;
                        setTimeout(function () {
                            // Date nav first (async grid load starts) then panel open
                            // (synchronous signal set — panel opens immediately).
                            dispatchConvoDetail(wcElForOpen, navDetailForDispatch);
                            wcElForOpen.dispatchEvent(new CustomEvent('meevo:open-appointment', {
                                detail: openDetailForDispatch,
                                bubbles: false,
                            }));
                        }, 0);
                    } else {
                        // Pre-mount: buffer date nav in pendingConvoDetail (consumed by
                        // preMountProps as initialConvoParams) and panel open in
                        // pendingOpenDetail (replayed in onMount after ngOnInit registers
                        // the externalOpenHandler listener).
                        pendingConvoDetail = navDetail;
                        pendingOpenDetail = openDetail;
                    }
                }
                return;
            }

            // ── Date ─────────────────────────────────────────────────
            var targetDate: string | null = null;

            if (params.dates && params.dates.length > 0) {
                var raw = params.dates[0];
                // A bare 'YYYY-MM-DD' must NOT be round-tripped through `new Date(...)`:
                // the ES spec parses date-only strings as UTC midnight, so any browser
                // west of UTC re-formats it back to the previous calendar day. Forward
                // the canonical local date verbatim instead. (Same guard used by
                // navigateToScheduleManager below.)
                if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                    targetDate = raw;
                } else {
                    var d = raw instanceof Date ? raw : new Date(raw);
                    if (!isNaN(d.getTime())) {
                        targetDate = toLocalIsoDate(d);
                    }
                }
            }

            if (!targetDate && params.dateAction) {
                var now = new Date();
                if (params.dateAction === 'tomorrow') {
                    now.setDate(now.getDate() + 1);
                }
                // Only 'tomorrow' offsets the date; 'today' and any unrecognised
                // dateAction values resolve to the current date (safe fallback).
                targetDate = toLocalIsoDate(now);
            }

            // ── View mode ────────────────────────────────────────────
            var viewMode: 'day' | 'week' = (params.dates && params.dates.length > 1) ? 'week' : 'day';

            // ── Employees ────────────────────────────────────────────
            // clearFilters mirrors legacy: absent employees → clear all filters
            // empty array → set empty employee filter only
            var clearFilters: boolean = (params.employees == null);
            var employeeIds: string[] = (params.employees || []).map(function (e: any) { return String(e.id); });

            // ── Gender ───────────────────────────────────────────────
            // Server sends GenderPreferenceEnum: 108 = MaleOnly, 109 = FemaleOnly.
            // #135330: service-scan convo commands ("<service> with male ...")
            // carry the gender inside scanRequest.scanServices[0].genderPreferenceEnum
            // rather than params.gender — mirror the legacy applyParams, which
            // reads both sources (AppointmentBookFilterActionsService.applyParams).
            var scanServices = params.scanRequest && params.scanRequest.scanServices;
            var scanGenderPreference = (scanServices && scanServices[0])
                ? scanServices[0].genderPreferenceEnum
                : null;
            var gender: number | null = (params.gender != null)
                ? params.gender
                : (scanGenderPreference == 108 || scanGenderPreference == 109) ? scanGenderPreference : null;

            // ── Price levels ─────────────────────────────────────────
            var priceLevels: number[] = params.priceLevels || [];

            // ── Time scroll ──────────────────────────────────────────
            var time: string | null = params.time || null;
            // Legacy param name is scrollToCurrentDate; outgoing contract uses scrollToCurrentTime.
            var scrollToCurrentTime: boolean = !!params.scrollToCurrentDate;

            var detail = {
                date: targetDate,
                viewMode: viewMode,
                employeeIds: employeeIds,
                clearFilters: clearFilters,
                gender: gender,
                priceLevels: priceLevels,
                time: time,
                scrollToCurrentTime: scrollToCurrentTime,
                columnHeaderOrder: params.columnHeaderOrder || null,
                // ADO #128771: convo.service attaches this when it rewrote
                // the convo input based on the logged-in user's
                // employeeAppointmentBookViewPreference (MyDay/MyWeek).
                // The web component routes employeeIds into a non-filter
                // signal when true, so the filter chip count does not
                // include the implicit default-view scope.
                isDefaultEmployeeAppointmentBookView: !!params.isDefaultEmployeeAppointmentBookView,
            };

            // Enhanced-mode parity with legacy AppointmentBookFilterActionsService.applyParams
            // (line ~141), which unconditionally forwards params.scanRequest to
            // suggestionsService.getSuggestions(). That call has no equivalent on this bridge, so
            // convo-driven suggestion scans (e.g. "haircut with john tomorrow") never populate the
            // <suggestions-indicator> badge/modal when the enhanced web component is mounted — those
            // components are global-nav-owned and already listen for APPOINTMENT_SUGGESTIONS_CHANGED
            // regardless of which AB renderer is active. getSuggestions() itself no-ops when
            // scanRequest is falsy, so this call is safe to make unconditionally here too.
            var suggestionsService = $injector.get<any>('meevo.components.suggestionsService');
            suggestionsService.getSuggestions(params.scanRequest);

            // Guard: if the element was removed from the DOM (user closed the
            // appointment book tab), mountedWcElement is orphaned — isConnected
            // returns false. Clear it so we fall through to the pending-detail
            // buffer path; onMount will replay the params when the component
            // remounts.
            if (mountedWcElement && !(mountedWcElement as any).isConnected) {
                mountedWcElement = null;
            }

            if (mountedWcElement) {
                // Component already mounted — defer out of the current AngularJS
                // $broadcast/$digest cycle before dispatching. Firing synchronously
                // inside $broadcast can interfere with Angular's zone and signal
                // reactivity, causing the date/filter updates to be silently dropped.
                // This mirrors the legacy $timeout() pattern in AppointmentBook.ts.
                var wcEl = mountedWcElement;
                setTimeout(function () { dispatchConvoDetail(wcEl, detail); }, 0);
            } else {
                // Component not yet mounted (bundle still loading or tab was
                // closed and is remounting). Buffer the detail; onMount will
                // replay it once the element exists and its event listener
                // (registered in ngOnInit) is attached.
                pendingConvoDetail = detail;
            }
        });

        // Return-from-navigation refresh: the web component now detects
        // this itself via an IntersectionObserver on its host element
        // (fires when ui-router removes ng-hide on $stateChangeSuccess).
        // No host-side dispatch is needed.

        webComponentSetup.register({
            id: 'appointment-book',

            // ── Feature Gate ────────────────────────────────────────────
            gateSource: 'businessInfoPreferences',
            gateResolver: function () {
                // Consume the one-shot switch-time flag: force a live re-fetch so
                // the gate decides against current settings, not the startup cache.
                // The mount directive's swap() has already flipped ctrl.loading on,
                // so the existing md-progress-circular spinner covers this fetch.
                var forceRevalidate = revalidateOnNextGate;
                revalidateOnNextGate = false;
                var options = forceRevalidate ? { force: true } : undefined;

                return businessInfoDataService.getBusinessInfoPreferences(options).then(function (preferences: any) {
                    preferences = preferences || {};

                    // Keep the banner's own settings snapshot in lockstep with the
                    // just-fetched preferences so shouldShow()/isOffered() re-evaluate
                    // against the same data the gate used — otherwise the banner keeps
                    // reading the stale startup snapshot.
                    if (forceRevalidate) {
                        appSettings.APP_BUSINFOSETTINGS = preferences;
                    }

                    var state = resolveExperienceState(preferences);
                    // Expose the latest state for diagnostics and legacy banner logic.
                    (window as any).__meevoAppointmentBookExperienceState = state;

                    // Just-in-time sanity check failed: the switch was requested, but a
                    // fresh read shows the enhanced book is no longer offered (another
                    // user flipped a gating setting). Revert the stored choice, tell the
                    // user, and fall through to legacy — shouldRenderEnhanced is already
                    // false because offered is false.
                    if (forceRevalidate && !state.offered) {
                        setStoredExperienceChoice(false);
                        notifyEnhancedNoLongerAvailable();
                    }

                    return {
                        appointmentBookSettings: {
                            enhancedBookingMode: state.shouldRenderEnhanced,
                        },
                    };
                });
            },

            // ── Pre-Mount Props ─────────────────────────────────────────
            // Returns buffered convo params (if any) as initialConvoParams.
            // Called by the mount directive BEFORE appendChild, so the
            // component has the correct date/filters from its very first
            // initialization cycle — no flash, no wasted API call.
            preMountProps: function () {
                if (!pendingConvoDetail) {
                    return null;
                }
                var detail = pendingConvoDetail;
                pendingConvoDetail = null;
                return { initialConvoParams: detail };
            },

            // ── Post-Mount Hooks ────────────────────────────────────────
            onMount: function (wcElement: HTMLElement) {
                mountedWcElement = wcElement;

                // #130189: Replay a pending EditAppointment open request. Date nav was
                // already handled via initialConvoParams (preMountProps consumed
                // pendingConvoDetail). This only opens the view-appointment panel.
                if (pendingOpenDetail) {
                    var pendingDetail = pendingOpenDetail;
                    pendingOpenDetail = null;
                    setTimeout(function () {
                        wcElement.dispatchEvent(new CustomEvent('meevo:open-appointment', {
                            detail: pendingDetail,
                            bubbles: false,
                        }));
                    }, 0);
                }

                if (pendingManualRebookServices) {
                    var pendingServices = pendingManualRebookServices;
                    pendingManualRebookServices = null;
                    setTimeout(function () {
                        dispatchManualRebook(wcElement, pendingServices);
                    }, 0);
                }

                // R-121880: The AB emits { date: 'YYYY-MM-DD', viewMode } on
                // scheduleEmployeeClick. The Schedule Manager runs in an interop iframe
                // child app, so we hand the date off via the generic host→child context
                // params pipe (InteropService.pushParamsToContext → xplat
                // HostContextParamsService). String detail (legacy ISO) and bare
                // no-detail calls fall through to the child's default (today).
                //
                // A plain 'YYYY-MM-DD' is intentionally forwarded as-is rather than
                // round-tripped through `new Date(...)` + toLocalIsoDate(), because
                // JavaScript parses bare date-only strings as UTC midnight. In any
                // local timezone west of UTC that re-local-formats to the previous
                // day — which would make the Schedule Manager open to the prior week.
                var DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
                function navigateToScheduleManager(detail?: any) {
                    var schedulesRoute: string = appSettings.ROUTE_NAMES.schedulesManager;
                    var rawDate: string | null = null;

                    if (typeof detail === 'string' && detail.length > 0) {
                        rawDate = detail;
                    } else if (detail && typeof detail === 'object') {
                        rawDate = (detail as any).date || (detail as any).currentDate || null;
                    }

                    if (rawDate) {
                        var currentDate: string | null = null;
                        if (DATE_ONLY_PATTERN.test(rawDate)) {
                            // Already a canonical local date — forward verbatim.
                            currentDate = rawDate;
                        } else {
                            var parsed = new Date(rawDate);
                            if (!isNaN(parsed.getTime())) {
                                currentDate = toLocalIsoDate(parsed);
                            }
                        }
                        if (currentDate) {
                            // sendContextParams picks the transport: querystring on
                            // the iframe URL when the Schedule Manager is about to be
                            // cold-opened, post-robot push when its tab is already
                            // open (GOTO_STATE then only selects the existing tab, so
                            // no URL is rebuilt and a querystring would never be read).
                            interopService.sendContextParams(schedulesRoute, {
                                currentDate: currentDate,
                            });
                        }
                    }

                    $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                        stateName: schedulesRoute,
                    });
                }

                // Angular Elements lowercases event names; listen for both casings
                wcElement.addEventListener('scheduleEmployeeClick', function (event: Event) {
                    navigateToScheduleManager((event as CustomEvent).detail);
                });
                wcElement.addEventListener('scheduleemployeeclick', function (event: Event) {
                    navigateToScheduleManager((event as CustomEvent).detail);
                });

                // R-VIEW-019: Send message — routes to MeevoMessenger (Textel/TwoWaySMS)
                // or Client Find's Send Text Message tab when no messaging integration is
                // active. The WC currently dispatches the camelCase name via
                // `dispatchEvent(new CustomEvent('sendMessage', ...))` — register both
                // casings (matches the scheduleEmployeeClick pattern above) so the
                // listener fires regardless of whether the dispatch side is normalised.
                function handleSendMessage(event: Event) {
                    const d = (event as CustomEvent).detail as {
                        clientId: string;
                        firstName: string | null;
                        lastName: string | null;
                        primaryPhoneNumber: string | null;
                        countryCode: string | null;
                        isRestricted: boolean;
                        isClient: boolean;
                        classification: 'TEXTEL' | 'SMS' | null;
                    };

                    $rootScope.$apply(function () {
                        if (d.classification) {
                            // Textel or TwoWaySMS — open MeevoMessenger
                            $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                                stateName: appSettings.ROUTE_NAMES.meevoMessenger,
                                stateParams: {
                                    id:                 d.clientId,
                                    firstName:          d.firstName,
                                    lastName:           d.lastName,
                                    primaryPhoneNumber: d.primaryPhoneNumber,
                                    countryCode:        d.countryCode,
                                    isRestricted:       d.isRestricted,
                                    isClient:           d.isClient,
                                    classification:     d.classification,
                                },
                            });
                        } else {
                            // No messaging integration — open Search sidenav then
                            // broadcast to Client Find "Send Text Message" tab.
                            // Mirrors legacy: sideContextMenu('Search').open().then(…)
                            $mdSidenav('Search').open().then(function () {
                                $rootScope.$broadcast(Meevo.Components.Common.Messages.OPEN_SEARCH_MENU_COMPLETE, {
                                    id: d.clientId,
                                    landingTab: Meevo.Model.NounEnum.ClientFindSendTextMsgTab,
                                });
                            });
                        }
                    });
                }
                wcElement.addEventListener('sendMessage', handleSendMessage);
                wcElement.addEventListener('sendmessage', handleSendMessage);

                // Launch form — mirrors legacy
                // `docsAndFormsService.showForm → GOTO_STATE(docsAndFormsKiosk)`
                // so the WC's per-form "Launch form" action opens the same
                // kiosk view that the legacy view-appointment popup uses.
                function handleLaunchForm(event: Event) {
                    const d = (event as CustomEvent).detail as {
                        formId: string;
                        formDisplayName: string;
                        formUrl: string;
                        clientId: string;
                        appointmentId: string;
                        appointmentServiceId: string;
                    };
                    if (!d || !d.formId) {
                        return;
                    }

                    $rootScope.$apply(function () {
                        $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                            stateName: appSettings.ROUTE_NAMES.docsAndFormsKiosk,
                            stateParams: {
                                form: {
                                    formDisplayName: d.formDisplayName,
                                    formId:          d.formId,
                                    formUrl:         d.formUrl,
                                    serviceId:       d.appointmentServiceId,
                                },
                                clientId:      d.clientId,
                                appointmentId: d.appointmentId,
                                callbackState: $state.current.name,
                            },
                        });
                    });
                }
                wcElement.addEventListener('launchForm', handleLaunchForm);
                wcElement.addEventListener('launchform', handleLaunchForm);

                // #120927: Checkout — navigate to Register
                function handleCheckout(event: Event) {
                    const d = (event as CustomEvent).detail;
                    if (!d || !d.appointmentId) return;
                    $rootScope.$apply(function () {
                        $rootScope.$broadcast('gotoState', {
                            stateName: 'register',
                            stateParams: {
                                action: Meevo.Components.Common.Messages.CHECKOUT_APPT_SERVICES,
                                appointmentId: d.appointmentId,
                                appointmentEntityVersion: d.appointmentEntityVersion,
                                id: d.serviceId,
                                services: d.services,
                                actionDetails: [{}],
                                pastTransactionContext: d.isPastAppointment ? {
                                    transactionDate: d.transactionDate,
                                    transactionTime: d.transactionTime,
                                    drawerId: d.drawerId,
                                    drawerSessionId: d.drawerSessionId,
                                    drawerName: d.drawerName,
                                } : undefined,
                            },
                        });
                    });
                }
                wcElement.addEventListener('launchRegisterCheckout', handleCheckout);
                wcElement.addEventListener('launchregistercheckout', handleCheckout);

                // M20-103 §9.6 / §10.6: Charge No-Show Fee — delegate to the legacy
                // `appointmentBookActionsService.launchRegisterCheckout(service, …)`.
                // That function already branches on `transitionState === NoShow`,
                // reads `globals.appointmentNoShowFeePercentage` /
                // `globals.applyNoShowFeeToAddOns`, constructs the
                // `noShowFeeDetail = { type: ServiceNoShowFee, fee, applyToAddOns }`,
                // pushes the service twice with parallel actionDetails
                // `[{}, noShowFeeDetail]`, performs the security check, adds the
                // recent client, builds `payingClient` + `isAnonymousClientCheckout`,
                // and broadcasts `gotoState → register` with the full stateParams
                // shape the Register expects. Reconstructing that here would risk
                // drifting from the canonical contract — and an earlier attempt
                // (hand-rolled gotoState) caused Register to ring up the full
                // service price instead of just the no-show fee.
                // M20-103 §9.6 / §10.6 — Charge no-show fee.
                //
                // Thin forwarder: the WC builds the complete Register
                // stateParams (services single-push, actionDetails with the
                // ServiceNoShowFee type, isAnonymousClientCheckout,
                // payingClient block, pastTransactionContext) and hands the
                // host a `{stateName, stateParams}` envelope. The host only
                // translates the envelope into the AngularJS `gotoState`
                // broadcast — no API fetches, no enum knowledge, no fee math.
                // Keeps the bridge portable across hosts; canonical stateParams
                // shape (action / appointmentId / id / services / actionDetails
                // / payingClient) lives WC-side.
                function handleNoShowFee(event: Event) {
                    const d = (event as CustomEvent).detail;
                    if (!d || !d.stateName || !d.stateParams) {
                        console.warn('[NoShowFeeBridge] aborting: event detail missing {stateName, stateParams}', d);
                        return;
                    }
                    console.log('[NoShowFeeBridge] forwarding gotoState', {
                        stateName: d.stateName,
                        actionDetailsCount: Array.isArray(d.stateParams.actionDetails) ? d.stateParams.actionDetails.length : 0,
                        servicesCount: Array.isArray(d.stateParams.services) ? d.stateParams.services.length : 0,
                    });
                    $rootScope.$apply(function () {
                        $rootScope.$broadcast('gotoState', {
                            stateName: d.stateName,
                            stateParams: d.stateParams,
                        });
                    });
                }
                wcElement.addEventListener('launchRegisterNoShowFee', handleNoShowFee);
                wcElement.addEventListener('launchregisternoshowfee', handleNoShowFee);

                wcElement.addEventListener('launchRegisterCancellationFee', handleNoShowFee);
                wcElement.addEventListener('launchregistercancellationfee', handleNoShowFee);

                // Client indicator tooltip link — open in a new tab. The WC builds
                // the complete `{stateName, stateParams}` envelope (targetState,
                // actionState, initialEditSection, initialEditSubSection, id, filter);
                // the host only translates it into the AngularJS gotoState broadcast,
                // mirroring the thin no-show / view-transaction forwarders above.
                function handleClientIndicatorGotoState(event: Event) {
                    const d = (event as CustomEvent).detail;
                    if (!d || !d.stateName || !d.stateParams) {
                        return;
                    }
                    $rootScope.$apply(function () {
                        $rootScope.$broadcast('gotoState', {
                            stateName: d.stateName,
                            stateParams: d.stateParams,
                        });
                    });
                }
                wcElement.addEventListener('clientIndicatorGotoState', handleClientIndicatorGotoState);
                wcElement.addEventListener('clientindicatorgotostate', handleClientIndicatorGotoState);

                // Payment Plan Manager (client "Active / all is well" indicator).
                // Payment Plans lives in the MeevoShellV2 interop iframe, not a native
                // AngularJS state, so it cannot go through handleClientIndicatorGotoState
                // (the gotoState handler hardcodes a cold interop context's targetRoute to
                // the bare state name, dropping the client filter). Instead push the client
                // id through InteropService — query param for a cold open, post-robot for a
                // live iframe — then broadcast GOTO_STATE, exactly like navigateToScheduleManager.
                function handlePaymentPlanManager(event: Event) {
                    const d = (event as CustomEvent).detail as { clientId?: string };
                    if (!d || !d.clientId) {
                        return;
                    }
                    var paymentPlansRoute: string = appSettings.ROUTE_NAMES.paymentPlans;
                    interopService.sendContextParams(paymentPlansRoute, { clientId: d.clientId });
                    $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                        stateName: paymentPlansRoute,
                    });
                }
                wcElement.addEventListener('openPaymentPlanManager', handlePaymentPlanManager);
                wcElement.addEventListener('openpaymentplanmanager', handlePaymentPlanManager);

                // #120927: View Transaction — navigate to Transaction Editor
                function handleViewTransaction(event: Event) {
                    const d = (event as CustomEvent).detail;
                    if (!d || !d.transactionId) return;
                    $rootScope.$apply(function () {
                        $rootScope.$broadcast('gotoState', {
                            stateName: appSettings.ROUTE_NAMES.transactionEditor,
                            stateParams: {
                                transactionId: Number(d.transactionId),
                                includeActive: true,
                                includeCompleted: true,
                                includeOnHold: false,
                                includeRefunded: true,
                                includeVoided: true,
                            },
                            securityKey: Meevo.Components.Common.SecurityKeys.REGISTER_TRANSACTIONEDITOR + Meevo.Components.Common.SecurityParts.KEY_PART_READ,
                        });
                    });
                }
                wcElement.addEventListener('viewTransaction', handleViewTransaction);
                wcElement.addEventListener('viewtransaction', handleViewTransaction);

                // #122499/#122500 — Print Ticket. The AB root re-emits the view panel's
                // `printDispatch` as `printAppointment` (flat payload) because Angular
                // Elements only surfaces @Outputs declared on the root component.
                //
                // #122500 delta: call `printWorkTicket` / `printWorkTicketReport` directly
                // (NOT `printWorkTicketFromBook`) so we own the post-print callback.
                // `printWorkTicketFromBook` internally calls `promptCheckIn()` (legacy
                // AngularJS modal); instead we feed `appointmentBookSettings` into the WC
                // and call `onPrintSucceeded()` which fires the refreshed Prism receipt
                // toast + check-in prompt (R-PT-6.1.3 / R-PT-6.1.4b).
                //   receipt → AQ022 thermal/PDF  (printWorkTicket)
                //   smallPdf → reportId 2         (printWorkTicketReport, AQ022 PDF viewer)
                //   largePdf → reportId 1         (printWorkTicketReport, AQ020)
                function handlePrintAppointment(event: Event) {
                    const d = (event as CustomEvent).detail as {
                        appointmentId: string;
                        clientId: string;
                        employeeId: string;
                        startDateTime: string;
                        option: 'receipt' | 'smallPdf' | 'largePdf';
                    };
                    if (!d || !d.appointmentId) return;
                    const actions = $injector.get<any>('appointmentBookActionsService');
                    $rootScope.$apply(function () {
                        actions.details = angular.extend(actions.details || {}, {
                            appointmentId: d.appointmentId,
                            clientId:      d.clientId,
                            employeeId:    d.employeeId,
                            startDateTime: d.startDateTime,
                        });
                        // ADO #122500 — suppress legacy ALERT! dialogs for the entire print
                        // chain (api/Settings/General + api/Report/AQ022). The WC shows a
                        // Prism error toast via printFailed @Input instead.
                        const $window = $injector.get<ng.IWindowService>('$window');
                        ($window as any).__abRefreshPrintActive = true;
                        const clearPrintFlag = () => { ($window as any).__abRefreshPrintActive = false; };

                        const printPromise: ng.IPromise<any> = d.option === 'receipt'
                            ? actions.printWorkTicketForRefresh(d.appointmentId)
                            : actions.printWorkTicketReport(d.option === 'largePdf' ? 1 : 2);

                        printPromise.then(function () {
                            clearPrintFlag();
                            // R-PT-6.1.3 receipt toast + R-PT-6.1.4b check-in prompt only for
                            // receipt. PDF success is the modal opening; no additional WC feedback.
                            if (d.option !== 'receipt') return;
                            actions.getAppointmentBookSettings().then(function (settings: Meevo.Model.AppointmentBookSettings) {
                                // Angular Elements exposes @Input() setters as DOM element
                                // properties. `printCompletedResult` setter in
                                // appointment-book.component.ts forwards to viewApptPanelRef
                                // to fire the receipt toast + check-in prompt.
                                (wcElement as any).printCompletedResult = {
                                    workTicketPromptCheckInOnPrint: settings.workTicketPromptCheckInOnPrint,
                                };
                            });
                        }).catch(function (rejection: any) {
                            clearPrintFlag();
                            // Route print errors to the WC as a Prism error toast.
                            // printWorkTicketForRefresh sets skipInterceptor:true so the
                            // ExceptionInterceptor passes the rejection through without
                            // showing the legacy ALERT! dialog (ADO #122500 user request).
                            const rs = rejection && rejection.data && rejection.data.responseStatus;
                            let messages: string[];
                            if (rs && rs.errors && rs.errors.length > 0) {
                                messages = (rs.errors as any[]).map((e: any) => e.message).filter(Boolean);
                            } else if (rs && rs.message) {
                                messages = [rs.message];
                            } else {
                                messages = ['Work ticket print failed. Check printer connection.'];
                            }
                            (wcElement as any).printFailed = { messages };
                        });
                    });
                }
                wcElement.addEventListener('printAppointment', handlePrintAppointment);
                wcElement.addEventListener('printappointment', handlePrintAppointment);

                // Cache the web component's current date/view so a switch-back can
                // seed the legacy book with the same context. The WC emits this via
                // its experienceStateChange output (Angular Elements lowercases the
                // DOM event name — listen for both casings).
                function handleExperienceStateChange(event: Event) {
                    var detail = (event as CustomEvent).detail as { date?: string; viewMode?: 'day' | 'week'; bookView?: WcBookView } | null;
                    if (detail && detail.date) {
                        lastWcExperienceState = {
                            date: detail.date,
                            viewMode: detail.viewMode === 'week' ? 'week' : 'day',
                            bookView: detail.bookView === 'resource' ? 'resource' : 'employee',
                        };
                    }
                }
                wcElement.addEventListener('experienceStateChange', handleExperienceStateChange);
                wcElement.addEventListener('experiencestatechange', handleExperienceStateChange);

                function handleSwitchBackToLegacy(): void {
                    setStoredExperienceChoice(false);
                    // Clear the in-memory dismiss flag so the banner shows immediately on return.
                    bannerService.undismiss();
                    // Buffer the WC's current date/view so the legacy controller can be
                    // seeded once it re-mounts (see the WEB_COMPONENT_MOUNTED listener).
                    pendingLegacySeedParams = buildLegacySeedParamsFromWcState();
                    // Re-gate the mount in place instead of reloading the page.
                    $rootScope.$broadcast(Meevo.Components.Common.Messages.WEB_COMPONENT_REGATE, {
                        componentId: 'appointment-book',
                    });
                }
                wcElement.addEventListener('switchBackToLegacyExperience', handleSwitchBackToLegacy);
                wcElement.addEventListener('switchbacktolegacyexperience', handleSwitchBackToLegacy);

                // #130068: After the legacy cancel modal (launched above for
                // Confirmation Manager / Client Find cancel) completes, the
                // legacy AppointmentBookActionsService broadcasts
                // 'APPOINTMENT DELETED' (line 546 of ActionsService).
                // Forward it to the WC so it refreshes the grid and removes
                // the cancelled appointment immediately.
                $rootScope.$on('APPOINTMENT DELETED', function () {
                    if (mountedWcElement && (mountedWcElement as any).isConnected) {
                        mountedWcElement.dispatchEvent(new CustomEvent('meevo:appointment-cancelled', {
                            bubbles: false,
                        }));
                    }
                });

                // #130189: WC fires this when an externally-triggered edit completes
                // (save or cancel). Mirrors legacy AppointmentBookHelperService.checkReturnToState:
                // reads returnStateSection (not stateName) + optional id/actionState/targetState,
                // then broadcasts GOTO_STATE and appointmentChanged (requireRefresh=true on save).
                wcElement.addEventListener('meevo:edit-complete', function (evt) {
                    var saved = !!(evt as CustomEvent).detail && !!(evt as CustomEvent).detail.saved;
                    var rsi = pendingReturnStateInfo;
                    pendingReturnStateInfo = null;
                    if (rsi && rsi.returnStateSection) {
                        $rootScope.$apply(function () {
                            $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                                stateName: rsi!.returnStateSection,
                                stateParams: {
                                    id: rsi!.id,
                                    initialEditSection: rsi!.initialEditSection,
                                    actionState: rsi!.actionState,
                                    targetState: rsi!.targetState,
                                },
                            });
                            $rootScope.$broadcast('appointmentChanged', {
                                stateParams: {
                                    requireRefresh: saved,
                                    stateName: rsi!.returnStateSection,
                                },
                            });
                        });
                    }
                });
            },

            // ── Pre-Unmount Hooks ───────────────────────────────────────
            // Runs just before the mount directive removes the element (route
            // away / $scope destroy). Element-scoped addEventListeners above
            // die with the element, so they don't need explicit teardown —
            // but the host-side `mountedWcElement` reference does. Clearing
            // it here ensures the APPLY_PARAMS_TO_CONTEXT listener falls
            // through to the pendingConvoDetail buffer path on the next
            // convo navigation instead of dispatching to a detached element.
            onUnmount: function (wcElement: HTMLElement) {
                if (mountedWcElement === wcElement) {
                    mountedWcElement = null;
                }
                pendingOpenDetail = null;
                pendingManualRebookServices = null;
                pendingReturnStateInfo = null;
            },
        });

        // ── Live experience swap (no reload) ────────────────────────────
        var FILTER_ACTIONS_TOKEN = 'Meevo.Plugins.AppointmentBook.Services.AppointmentBookFilterActionsService';

        // Read the legacy book's current date/view synchronously and shape it as a
        // ConvoParamsDetail so the incoming web component boots on the same context.
        function buildConvoDetailFromLegacy(): any {
            var filterActions: any = $injector.get(FILTER_ACTIONS_TOKEN);
            var globals: any = $injector.get('appointmentBookGlobalsService');

            var dates: any[] = (filterActions.dates && filterActions.dates.length)
                ? filterActions.dates
                : (filterActions.dateString ? [filterActions.dateString] : []);
            var first = dates.length ? dates[0] : new Date();
            var firstDate = first instanceof Date ? first : new Date(first);
            var isWeek = (globals && globals.isDayView === false) || (dates && dates.length > 1);

            var employeeIds: string[] = (filterActions.filteredEmployees || []).map(function (e: any) { return String(e.id); });

            var gender: number | null = null;
            if (filterActions.gender === Meevo.Model.GenderEnum.Female) {
                gender = 109;
            } else if (filterActions.gender === Meevo.Model.GenderEnum.Male) {
                gender = 108;
            }

            return {
                date: toLocalIsoDate(firstDate),
                viewMode: isWeek ? 'week' : 'day',
                employeeIds: employeeIds,
                clearFilters: employeeIds.length === 0,
                gender: gender,
                priceLevels: filterActions.filteredPriceLevels || [],
                time: null,
                scrollToCurrentTime: false,
                columnHeaderOrder: filterActions.columnHeaderOrder || null,
                isDefaultEmployeeAppointmentBookView: false,
                bookView: (globals && globals.isEmployeeView === false) ? 'resource' : 'employee',
            };
        }

        // Expand a base date into the 7 dates of its week, honouring the business's
        // configured start-of-week. Mirrors AppointmentBookFilterActionsService.recalculateDate.
        function expandWeekDates(base: Date, startDayOfWeek: number): Date[] {
            var d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
            var diff = d.getDay() - startDayOfWeek;
            if (diff < 0) {
                diff += 7;
            }
            d.setDate(d.getDate() - diff);
            var week: Date[] = [];
            for (var i = 0; i < 7; i++) {
                week.push(new Date(d.getFullYear(), d.getMonth(), d.getDate() + i));
            }
            return week;
        }

        // Build the applyParams payload that seeds the legacy book from the WC's
        // last-known date/view. Returns null when we have no captured state.
        function buildLegacySeedParamsFromWcState(): { dates: Date[]; scrollToCurrentDate: boolean; isEmployeeView: boolean } | null {
            if (!lastWcExperienceState || !lastWcExperienceState.date) {
                return null;
            }
            var parts = lastWcExperienceState.date.split('-').map(Number);
            if (parts.length !== 3 || parts.some(isNaN)) {
                return null;
            }
            var base = new Date(parts[0], parts[1] - 1, parts[2]);
            var dates: Date[];
            if (lastWcExperienceState.viewMode === 'week') {
                var filterActions: any = $injector.get(FILTER_ACTIONS_TOKEN);
                dates = expandWeekDates(base, filterActions.getStartDayOfWeek());
            } else {
                dates = [base];
            }
            return {
                dates: dates,
                scrollToCurrentDate: false,
                isEmployeeView: lastWcExperienceState.bookView !== 'resource',
            };
        }

        // Legacy → new: capture the current legacy date/view, seed the incoming web
        // component via the pending-convo buffer (consumed by preMountProps), then
        // re-gate the mount so it renders the new experience in place.
        $rootScope.$on(Meevo.Components.Common.Messages.APPOINTMENT_BOOK_SWITCH_TO_NEW, function () {
            try {
                pendingConvoDetail = buildConvoDetailFromLegacy();
            } catch (error) {
                console.warn('[AppointmentBook] failed to capture legacy state for swap', error);
            }
            // Force the re-gate to sanity-check the gating settings against the live
            // API before mounting the enhanced book (in case another user disabled it
            // since page load). The re-gate runs under the mount spinner.
            revalidateOnNextGate = true;
            $rootScope.$broadcast(Meevo.Components.Common.Messages.WEB_COMPONENT_REGATE, {
                componentId: 'appointment-book',
            });
        });

        // New → legacy: once the legacy controller has re-mounted (and run its own
        // setDefaults()), apply the buffered date/view via applyParams — the same
        // programmatic path the convo pipeline uses.
        $rootScope.$on(Meevo.Components.Common.Messages.WEB_COMPONENT_MOUNTED, function (_evt, payload: { componentId?: string; mode?: string } | undefined) {
            if (!payload || payload.componentId !== 'appointment-book' || payload.mode !== 'legacy') {
                return;
            }
            if (!pendingLegacySeedParams) {
                return;
            }
            var params = pendingLegacySeedParams;
            pendingLegacySeedParams = null;
            $rootScope.$applyAsync(function () {
                try {
                    // applyParams ends in filterBook(), which reads globals.isEmployeeView
                    // to pick the employee or resource book — set it first.
                    var globals: any = $injector.get('appointmentBookGlobalsService');
                    globals.isEmployeeView = params.isEmployeeView;
                    var filterActions: any = $injector.get(FILTER_ACTIONS_TOKEN);
                    filterActions.applyParams({ dates: params.dates, scrollToCurrentDate: params.scrollToCurrentDate });
                } catch (error) {
                    console.warn('[AppointmentBook] failed to seed legacy book on swap', error);
                }
            });
        });
    }
]);
